// components/features/depth/DepthCard.js — PREMIUM DEPTH CARD
// ═══════════════════════════════════════════════════════════
// Weekly intelligence briefing card. Structured sections with
// collapsible panels, stats row, flow pipeline, risk bars.
// Feels like a premium report, not a text document.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

const C = {
  gold: '#c8a96e', sage: '#7a9e82', purple: '#9b8fc4', terra: '#c46050',
};

export default function DepthCard({ data, onSave, onAskShift, onNextSession }) {
  const [openSections, setOpenSections] = useState({ s1: true, s2: true, s3: true });
  const toggle = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  if (!data) return null;

  const {
    issueNumber, framework, sessionNumber, sessionContext, timeAgo,
    title, subtitle, constraint, stats, systemMap, verification, fixes, risk, user,
  } = data;

  return (
    <div style={s.card}>
      {/* Header band */}
      <div style={s.headerBand}>
        <div style={s.hbTop}>
          <div style={s.weeklyPill}>
            <div style={s.wpDot} />
            <span style={s.wpLabel}>Weekly depth · Issue {issueNumber || 1}</span>
          </div>
          <div style={s.hbMeta}>
            <span style={s.fwBadge}>{framework}</span>
            <div style={s.metaSep} />
            <span style={s.metaText}>{timeAgo}</span>
            {sessionNumber && (
              <>
                <div style={s.metaSep} />
                <span style={s.metaText}>Session #{sessionNumber}</span>
              </>
            )}
          </div>
        </div>
        <h2 style={s.hbTitle}>{title}</h2>
        {subtitle && <p style={s.hbSub}>{subtitle}</p>}
      </div>

      {/* Constraint hero */}
      {constraint && (
        <div style={s.constraintHero}>
          <SectionLabel label="The constraint" />
          <div style={s.constraintBox}>
            <div style={s.cbIcon}>&#x2297;</div>
            <div style={s.cbBody}>
              <div style={s.cbTitle}>{constraint.title}</div>
              <div style={s.cbDesc}>{constraint.description}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats row */}
      {stats && stats.length > 0 && (
        <div style={{ ...s.statsRow, gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, 1fr)` }}>
          {stats.map((stat, i) => (
            <div key={i} style={{
              ...s.statCell,
              borderRight: i < stats.length - 1 ? '1px solid #141414' : 'none',
            }}>
              <div style={{ ...s.statVal, color: stat.color || C.gold }}>{stat.value}</div>
              <div style={s.statLabel}>{stat.label}</div>
              {stat.sub && <div style={s.statSub}>{stat.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Section 1: System map */}
      {systemMap && (
        <CollapsibleSection
          num="1" color={C.gold}
          colorBg="rgba(200,169,110,0.1)" colorBorder="rgba(200,169,110,0.2)"
          title="The system map" open={openSections.s1} onToggle={() => toggle('s1')}
        >
          {systemMap.intro && <p style={s.bodyText}>{systemMap.intro}</p>}
          {systemMap.pipeline && <FlowPipeline steps={systemMap.pipeline} />}
          {systemMap.conclusion && (
            <p style={s.bodyText} dangerouslySetInnerHTML={{ __html: systemMap.conclusion }} />
          )}
        </CollapsibleSection>
      )}

      {/* Section 2: Verification */}
      {verification && verification.length > 0 && (
        <CollapsibleSection
          num="2" color={C.purple}
          colorBg="rgba(155,143,196,0.1)" colorBorder="rgba(155,143,196,0.2)"
          title="Verification — does this hold under pressure?" open={openSections.s2} onToggle={() => toggle('s2')}
        >
          {verification.map((item, i) => (
            <div key={i} style={s.verifyRow}>
              <div style={{ ...s.vrDot, background: item.color || C.purple }} />
              <div style={s.vrText} dangerouslySetInnerHTML={{ __html: item.text }} />
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* Section 3: The fix */}
      {fixes && fixes.length > 0 && (
        <CollapsibleSection
          num="3" color={C.sage}
          colorBg="rgba(122,158,130,0.1)" colorBorder="rgba(122,158,130,0.2)"
          title={`The fix — ${fixes.length} moves, in order`} open={openSections.s3} onToggle={() => toggle('s3')}
        >
          {fixes.map((fix, i) => (
            <FixStep key={i} num={i + 1} fix={fix} />
          ))}
          {risk && risk.length > 0 && <RiskBar risk={risk} />}
        </CollapsibleSection>
      )}

      {/* Footer */}
      <div style={s.footer}>
        <div style={s.footerLeft}>
          {user?.initials && <div style={s.footerAvatar}>{user.initials}</div>}
          <span style={s.footerName}>
            {user?.name || 'User'}{sessionNumber ? ` · Session #${sessionNumber}` : ''}{sessionContext ? ` · ${sessionContext}` : ''}
          </span>
        </div>
        <div style={s.footerRight}>
          {onSave && <FootBtn label="Save" onClick={onSave} />}
          {onAskShift && (
            <FootBtn label="Ask about this" color={C.purple} border="rgba(155,143,196,0.25)" bg="rgba(155,143,196,0.06)" onClick={onAskShift} />
          )}
          {onNextSession && (
            <FootBtn label="Next session &#x2192;" color={C.gold} border="rgba(200,169,110,0.25)" bg="rgba(200,169,110,0.06)" onClick={onNextSession} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──

function SectionLabel({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#444', fontFamily: 'Georgia, serif' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: '#141414' }} />
    </div>
  );
}

function CollapsibleSection({ num, color, colorBg, colorBorder, title, open, onToggle, children }) {
  return (
    <div style={{ padding: '18px 22px', borderBottom: '1px solid #141414' }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: open ? 14 : 0, cursor: 'pointer' }}>
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, flexShrink: 0, fontFamily: 'Georgia, serif',
          background: colorBg, border: `1px solid ${colorBorder}`, color,
        }}>
          {num}
        </div>
        <span style={{ fontSize: 13, color: '#d4d4d4', fontWeight: 'normal', flex: 1, fontFamily: 'Georgia, serif' }}>
          {title}
        </span>
        <span style={{
          fontSize: 10, color: '#333',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s', display: 'inline-block',
        }}>
          &#x25B6;
        </span>
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function FlowPipeline({ steps }) {
  const colors = [
    { color: C.gold, border: 'rgba(200,169,110,0.25)', bg: 'rgba(200,169,110,0.06)' },
    { color: C.purple, border: 'rgba(155,143,196,0.22)', bg: 'rgba(155,143,196,0.05)' },
    { color: C.sage, border: 'rgba(122,158,130,0.22)', bg: 'rgba(122,158,130,0.05)' },
    { color: C.gold, border: 'rgba(200,169,110,0.22)', bg: 'rgba(200,169,110,0.05)' },
    { color: '#444', border: '#222', bg: '#111' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0, margin: '4px 0' }}>
      {steps.map((step, i) => {
        const c = colors[i % colors.length];
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              fontSize: 11, padding: '5px 12px', borderRadius: 6,
              border: `1px solid ${c.border}`, background: c.bg, color: c.color,
              fontFamily: 'Georgia, serif', whiteSpace: 'nowrap',
            }}>
              {step}
            </div>
            {i < steps.length - 1 && (
              <span style={{ fontSize: 11, color: '#2a2a2a', padding: '0 4px' }}>&#x2192;</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FixStep({ num, fix }) {
  const colors = [
    { color: C.gold, border: 'rgba(200,169,110,0.2)', bg: 'rgba(200,169,110,0.1)' },
    { color: C.purple, border: 'rgba(155,143,196,0.2)', bg: 'rgba(155,143,196,0.1)' },
    { color: C.sage, border: 'rgba(122,158,130,0.2)', bg: 'rgba(122,158,130,0.1)' },
  ];
  const c = colors[(num - 1) % colors.length];
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start',
      padding: '10px 12px', background: '#07070a',
      border: '1px solid #272736', borderRadius: 8,
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, background: c.bg, border: `1px solid ${c.border}`, color: c.color,
        fontFamily: 'Georgia, serif',
      }}>
        {num}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: c.color, marginBottom: 3, fontFamily: 'Georgia, serif' }}>
          {fix.title}
        </div>
        <div style={{ fontSize: 11, color: '#555', lineHeight: 1.6, fontFamily: 'Georgia, serif' }}>
          {fix.description}
        </div>
      </div>
    </div>
  );
}

function RiskBar({ risk }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: '#333', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8, fontFamily: 'Georgia, serif' }}>
        Risk of fixing it
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {risk.map((item, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 5 }}>
              <span style={{ color: item.color, fontFamily: 'Georgia, serif' }}>{item.label}</span>
              <span style={{ color: '#444', fontFamily: 'Georgia, serif' }}>{item.level}</span>
            </div>
            <div style={{ height: 6, background: '#141414', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${item.pct}%`, background: item.fillColor, borderRadius: 3 }} />
            </div>
            {item.note && (
              <div style={{ fontSize: 10, color: '#444', fontStyle: 'italic', marginTop: 6, lineHeight: 1.5, fontFamily: 'Georgia, serif' }}>
                {item.note}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FootBtn({ label, color = '#444', border = '#272736', bg = 'transparent', onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontSize: 10, padding: '5px 12px', borderRadius: 6,
        border: `1px solid ${border}`, background: hov ? '#141414' : bg,
        color, cursor: 'pointer', fontFamily: 'Georgia, serif',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// ── Styles ──

const s = {
  card: {
    background: '#07070a', border: '1px solid #272736',
    borderRadius: 14, overflow: 'hidden',
    maxWidth: 780, margin: '0 auto', fontFamily: 'Georgia, serif',
  },
  headerBand: {
    background: '#07070a', borderBottom: '1px solid #141414', padding: '16px 22px 14px',
  },
  hbTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 10, flexWrap: 'wrap', gap: 8,
  },
  weeklyPill: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(200,169,110,0.08)', border: '1px solid rgba(200,169,110,0.25)',
    borderRadius: 20, padding: '4px 12px',
  },
  wpDot: { width: 5, height: 5, borderRadius: '50%', background: '#c8a96e' },
  wpLabel: { fontSize: 10, color: '#c8a96e', letterSpacing: '0.12em', textTransform: 'uppercase' },
  hbMeta: { display: 'flex', alignItems: 'center', gap: 10 },
  metaSep: { width: 1, height: 10, background: '#272736' },
  metaText: { fontSize: 10, color: '#2e2e2e' },
  fwBadge: {
    fontSize: 9, padding: '2px 8px', borderRadius: 4,
    border: '1px solid rgba(155,143,196,0.25)', background: 'rgba(155,143,196,0.07)',
    color: '#9b8fc4', letterSpacing: '0.06em',
  },
  hbTitle: {
    fontSize: 22, color: '#d4d4d4', fontWeight: 'normal',
    lineHeight: 1.25, marginBottom: 6, fontFamily: 'Georgia, serif',
  },
  hbSub: { fontSize: 12, color: '#444', fontStyle: 'italic', fontFamily: 'Georgia, serif', margin: 0 },
  constraintHero: {
    padding: '20px 22px', borderBottom: '1px solid #141414', background: '#07070a',
  },
  constraintBox: {
    background: 'rgba(196,96,80,0.05)', border: '1px solid rgba(196,96,80,0.2)',
    borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start',
  },
  cbIcon: {
    width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginTop: 1,
    background: 'rgba(196,96,80,0.1)', border: '1px solid rgba(196,96,80,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, color: '#c46050',
  },
  cbBody: { flex: 1 },
  cbTitle: { fontSize: 13, color: '#d4d4d4', marginBottom: 4, lineHeight: 1.4, fontFamily: 'Georgia, serif' },
  cbDesc: { fontSize: 11, color: '#666', lineHeight: 1.65, fontFamily: 'Georgia, serif' },
  statsRow: { display: 'grid', borderBottom: '1px solid #141414' },
  statCell: { padding: '14px 16px', textAlign: 'center' },
  statVal: { fontSize: 22, fontWeight: 'normal', marginBottom: 3, fontFamily: 'Georgia, serif' },
  statLabel: { fontSize: 9, color: '#333', textTransform: 'uppercase', letterSpacing: '0.1em', lineHeight: 1.4 },
  statSub: { fontSize: 10, fontStyle: 'italic', marginTop: 2, color: '#444', fontFamily: 'Georgia, serif' },
  bodyText: { fontSize: 12, color: '#777', lineHeight: 1.75, fontFamily: 'Georgia, serif', margin: 0 },
  verifyRow: {
    display: 'flex', gap: 8, alignItems: 'flex-start',
    padding: '8px 10px', background: '#111', border: '1px solid #1e1e2a', borderRadius: 7,
  },
  vrDot: { width: 5, height: 5, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  vrText: { fontSize: 12, color: '#777', lineHeight: 1.65, flex: 1, fontFamily: 'Georgia, serif' },
  footer: {
    padding: '14px 22px', borderTop: '1px solid #141414',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#07070a', flexWrap: 'wrap', gap: 8,
  },
  footerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  footerAvatar: {
    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
    background: 'rgba(200,169,110,0.12)', border: '1px solid rgba(200,169,110,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, color: '#c8a96e',
  },
  footerName: { fontSize: 11, color: '#555', fontFamily: 'Georgia, serif' },
  footerRight: { display: 'flex', gap: 6, flexWrap: 'wrap' },
};
